import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2
} from '@angular/core';

import { BaseComponent } from '@ridango/core';

import { fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import * as L from 'leaflet';
import { LatLngType, MapApiService, MapEvents } from '../services';
import { MapPopupComponent } from './map-popup.component';
import { IconSizeEnum, IconUtils } from '../services/map-icon.utils';
import { NavigationEnd, Router } from '@angular/router';
import { ApplicationFacade } from '@ridango/components';

let id = 0;

@Component({
    selector: 'rwidget-map',
    styles: [
        `
            .map-wrapper {
                height: 100%;
            }

            .map {
                min-height: 250px;
            }

            .map,
            .map-wrapper {
                display: flex;
                flex-grow: 1;
                flex-direction: column;
            }

            @media (min-width: 768px) {
                .map {
                    height: 100%;
                }
            }
        `
    ],
    template: ` <div [class]="'map-wrapper ' + (iconSizeClass || '')" [ngClass]="{ 'rounded-controls': !hasControls }">
        <div class="map" id="map-{{ mapId }}" [class.!h-full]="env.IS_MOBILE_APP"></div>
    </div>`,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    private mapReady$ = this.mapApi.mapReady().pipe(filter((ready) => ready));
    private zoom$ = new Subject<number>();

    @Input() latitude = 0;

    @Input() longitude = 0;

    /**
     * The zoom level of the map. The default zoom level is 13.
     */
    @Input() zoom = this.env.MAP?.INITIAL_ZOOM_MAP || 13;

    /**
     * The minimal zoom level of the map allowed. When not provided, no restrictions to the zoom level
     * are enforced.
     */
    @Input() minZoom = 3;

    /**
     * The maximal zoom level of the map allowed. When not provided, no restrictions to the zoom level
     * are enforced.
     */
    @Input() maxZoom: number = this.env.MAP?.MAX_ZOOM ?? 17;

    /**
     * If false, disables scrollwheel zooming on the map. The scrollwheel is enabled by default.
     */
    @Input() scrollwheel = true;

    /**
     * True to show layer controls and false to hide the controls
     */
    @Input() hasControls = !this.env.MAP?.ZOOM_CONTROLS_HIDDEN;

    /**
     * True to show default layer control and false to hide the control
     */
    @Input() hasDefaultLayersControl = false;

    /**
     * True to show map view settings control and false to hide the control
     */
    @Input() hasViewSettingsControl = false;

    /**
     * Set true to allow clicking on the map to return point coordinates. Coordinates emitted via output event
     * 'pointClicked'
     */
    @Input() allowPointClick = false;

    @Input() set fullMap(isFull: boolean) {
        this.zone.runOutsideAngular(() => {
            // isFull ? this.mapApi.zoomIn() : this.mapApi.zoomOut();
            this.mapApi.doResize();
        });
    }

    /**
     * Uses map without tiles
     */
    @Input() hiddenMap = false;

    @Output() pointClicked = new EventEmitter<LatLngType>();

    @Output() mapReady = new EventEmitter<void>();

    @Output() zoomChanged = new EventEmitter<number>();
    @Output() currentLocation = new EventEmitter<L.LocationEvent>();
    @ContentChildren(MapPopupComponent, { descendants: false }) popupComponent: QueryList<MapPopupComponent>;

    mapId = id++;
    iconSizeClass;
    zoomChangeEventHandler;
    isContrast$ = this.applicationFacade.highContrast$;

    constructor(
        @Inject('ENV') public env,
        private mapApi: MapApiService,
        public eRef: ElementRef,
        public zone: NgZone,
        private router: Router,
        private renderer: Renderer2,
        private ref: ChangeDetectorRef,
        private applicationFacade: ApplicationFacade,
        private ngZone: NgZone
    ) {
        super();
    }

    ngOnInit(): void {
        //Add no-margin class to body to all apps except the ones that use Tailwind
        //Removed for apps that use Tailwind because it breaks page structure on /book-a-ride page
        //Other apps styling relies on this class because their footer uses position: absolute;
        if (!this.env.USE_TAILWIND) {
            this.renderer.addClass(document.body, 'no-margin');
        }

        this.mapReady$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => this.zone.runOutsideAngular(() => this.init()));

        fromEvent(window.document, 'keydown')
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((event: KeyboardEvent) => {
                if (event.key === 'Escape' || event.key === 'Esc') {
                    this.mapApi.closePopup();
                }
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                delay(1200),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(() => {
                this.addAriaLabelToPolyline();
            });
    }

    ngAfterViewInit(): void {
        this.zone.runOutsideAngular(() => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const map = this.mapApi.createMap(
                `map-${this.mapId}`,
                {
                    center: { lat: this.latitude, lng: this.longitude },
                    zoom: this.zoom,
                    minZoom: this.minZoom,
                    maxZoom: this.maxZoom,
                    scrollWheelZoom: this.scrollwheel,
                    tap: false,
                    zoomControl: this.hasControls,
                    zoomSnap: 1,
                    zoomDelta: 2,
                    wheelPxPerZoomLevel: 150,
                    wheelDebounceTime: 50
                },
                this.hiddenMap,
                this.env.MAP?.LAYER_STYLES,
                this.hasViewSettingsControl
            );

            // TODO is possible find in future map provider which matches our contrast mode requirements and is free to use without rate limits
            // this.isContrast$.pipe(take(1)).subscribe((isContrast) => {
            //     if (isContrast) {
            //         // Available layers: https://leaflet-extras.github.io/leaflet-providers/preview/
            //         L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}.png', {
            //             minZoom: 0,
            //             maxZoom: 20,
            //             attribution:
            //                 '&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            //         }).addTo(map);
            //     }
            // });

            this.mapReady.emit();

            this.mapApi.showControls(this.hasDefaultLayersControl);
        });

        // visible on desktop, so shouldn't be applied
        if (this.env.IS_MOBILE_APP) {
            // these elements shouldn't be accessible for voiceover/talkback
            document.querySelectorAll('.leaflet-control-attribution').forEach((el) => {
                el.setAttribute('aria-hidden', 'true');
            });
        }
    }

    ngOnDestroy(): void {
        this.mapApi.removeAllListeners();
        this.mapApi.destroyMap();
        this.renderer.removeClass(document.body, 'no-margin');
        this.mapApi.removeListener(MapEvents.zoomEnd, this.zoomChangeEventHandler);
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();

        super.ngOnDestroy();
    }

    private init() {
        setTimeout(() => this.mapApi.doResize(), 100);

        if (this.allowPointClick) {
            this.registerClickEventSubscription();
        }

        this.registerClickOutEventSubscription();

        this.mapReady.emit();
        this.removeTabindexFromMap();

        this.initZoom();
    }

    private initZoom(): void {
        const iconSizeClasses = {
            [IconSizeEnum.small]: 'icon-size-small',
            [IconSizeEnum.medium]: 'icon-size-medium',
            [IconSizeEnum.large]: 'icon-size-large'
        };
        const zoomWithSize$ = this.getZoomWithSize$();

        zoomWithSize$.subscribe(({ zoom }) => {
            this.zoomChanged.emit(zoom);
        });

        zoomWithSize$.pipe(distinctUntilChanged((prev, curr) => prev.size === curr.size)).subscribe(({ size }) => {
            this.ngZone.run(() => {
                this.iconSizeClass = iconSizeClasses[size];
                this.ref.detectChanges();
            });
        });

        this.initZoomChangeHandler();
    }

    registerClickOutEventSubscription(): void {
        fromEvent(window.document, 'click')
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((event) => {
                if (!this.eRef.nativeElement.contains(event.target)) {
                    this.mapApi.closePopup();
                }
            });
    }

    registerClickEventSubscription(): void {
        this.zone.runOutsideAngular(() => {
            this.mapApi
                .createClickEventSubscription()
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((e) => {
                    this.zone.run(() => this.pointClicked.emit(e.latlng));
                    this.bindPopup(e.latlng);

                    if (this.env.EXPLORE?.SELECTED_BUS_STOPPED) {
                        this.mapApi.closePopup();
                    }
                });
        });
    }

    addAriaLabelToPolyline() {
        const polylinePoint = document.querySelectorAll('.leaflet-marker-icon');
        const polylinePointIcon = document.querySelectorAll('.leaflet-div-icon');

        polylinePoint?.forEach((polylinePointer, index) => {
            if (polylinePointer && polylinePointer.tagName !== 'IMG') {
                polylinePointer?.setAttribute('aria-label', 'Polyline pointer for point of interest ' + (index + 1));
            }
        });

        polylinePointIcon?.forEach((polylinePointer, index) => {
            if (polylinePointer && polylinePointer.tagName !== 'IMG') {
                polylinePointer?.setAttribute('aria-label', 'Polyline pointer for point of interest ' + (index + 1));
            }
        });
    }

    private initZoomChangeHandler(): void {
        this.zoomChangeEventHandler = () => {
            const zoom = this.mapApi.getNativeMap().getZoom();
            this.zoom$.next(zoom);
        };

        this.zoomChangeEventHandler();

        this.ngZone.runOutsideAngular(() => {
            this.mapApi.addListener(MapEvents.zoomEnd, this.zoomChangeEventHandler);
        });
    }

    private getZoomWithSize$(): Observable<{ zoom: number; size: IconSizeEnum }> {
        return this.zoom$.pipe(
            debounceTime(100),
            map((zoom: number) => ({
                zoom,
                size: IconUtils.getIconSize(zoom)
            }))
        );
    }

    private bindPopup(latlng: LatLngType): void {
        const popup = this.popupComponent.toArray()[0];
        if (popup) {
            popup.coordinates = latlng;
        }
    }

    private removeTabindexFromMap() {
        // Tabindex must be removed from the map after the map is ready otherwise it will que up hundreds of focusable
        // elements on the map and lag the screen readers when clicked on map
        setTimeout(() => {
            (document.querySelector('.map') as HTMLElement)?.removeAttribute('tabindex');
        });
    }
}
